'use client';

import { c as _c } from "react/compiler-runtime";
import type { Nullable, Options } from 'nuqs';
import type { ReactNode } from 'react';
import { createContext, useContext } from 'react';
import type { UrlStateStorageOptions } from './merge-state-with-storage';
import { URL_KEYS, type UrlState, urlStateSchema } from './url-state-schema';
import type { UseUrlStateStorageOptions } from './use-url-state-storage';
import { useUrlStateStorage } from './use-url-state-storage';
type UrlStateContextValue = {
  urlState: UrlState;
  updateUrlState: (updates: Partial<Nullable<UrlState>>, options?: Options) => void;
  isInitialized: boolean;
};
const UrlStateContext = createContext<UrlStateContextValue | null>(null);
const DEFAULT_PERSIST: (keyof UrlState)[] = ['from', 'to', 'adults', 'children', 'allocation'];
type UrlStateProviderProps = {
  children: ReactNode;
  defaultValue?: UrlState;
  initialUrlSync?: UrlStateStorageOptions<UrlState>['initialUrlSync'];
  options?: Omit<UseUrlStateStorageOptions<typeof urlStateSchema>, 'schema'>;
  persist?: (keyof UrlState)[];
};
export function UrlStateProvider(t0) {
  const $ = _c(10);
  const {
    children,
    defaultValue,
    initialUrlSync: t1
  } = t0;
  const initialUrlSync = t1 === undefined ? false : t1;
  let t2;
  if ($[0] !== defaultValue || $[1] !== initialUrlSync) {
    t2 = {
      schema: urlStateSchema,
      storage: {
        persist: DEFAULT_PERSIST,
        defaultValue,
        initialUrlSync,
        shouldUseStorageValue
      },
      urlKeys: URL_KEYS
    };
    $[0] = defaultValue;
    $[1] = initialUrlSync;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const [urlState, updateUrlState, t3] = useUrlStateStorage(t2);
  const {
    isInitialized
  } = t3;
  let t4;
  if ($[3] !== isInitialized || $[4] !== updateUrlState || $[5] !== urlState) {
    t4 = {
      urlState,
      updateUrlState,
      isInitialized
    };
    $[3] = isInitialized;
    $[4] = updateUrlState;
    $[5] = urlState;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const value = t4;
  let t5;
  if ($[7] !== children || $[8] !== value) {
    t5 = <UrlStateContext.Provider value={value}>{children}</UrlStateContext.Provider>;
    $[7] = children;
    $[8] = value;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
export function useUrlStateContext() {
  const context = useContext(UrlStateContext);
  if (!context) {
    throw new Error("useUrlStateContext must be used within UrlStateProvider");
  }
  return context;
}
const shouldUseStorageValue = (key: string, searchParams: URLSearchParams) => {
  // When "to" is not set, but "from" is set, we don't want to use the persisted value
  // because otherwise the date range will be set to the persisted value instead
  // of a single "from" date
  if (key === 'to' && searchParams.has('from') && !searchParams.has('to')) return false;
  return true;
};